import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ProductSubtype } from "src/app/ui/models/domains/pbm/Benefit/product-subtype";
import { ProductType } from "src/app/ui/models/domains/pbm/Benefit/product-type";
import { FiscalNumber } from "src/app/ui/models/domains/pbm/fiscal-number";
import { PriceRegime } from "src/app/ui/models/domains/pbm/product-management/price-regime";
import { ProductLabelInfo } from "src/app/ui/models/domains/pbm/product-management/product-label-info";
import { ProductUseValidity } from "src/app/ui/models/domains/pbm/product-management/product-use-validity";
import { TributaryCreditConcession } from "src/app/ui/models/domains/pbm/product-management/tributary-credit-concession";
import { UseType } from "src/app/ui/models/domains/pbm/product-management/use-type";

export class ProductLabelInfoForm {
  private formGroup: UntypedFormGroup;

  constructor() {}

  public getFormGroup(): UntypedFormGroup {
    return this.formGroup;
  }

  public createProductFormGroup(): void {
    const formBuilder = new UntypedFormBuilder();
    this.formGroup = formBuilder.group({
      productKey: new UntypedFormControl(""),
      ean1: new UntypedFormControl({ value: "" }, Validators.required),
      ean2: new UntypedFormControl({ value: "" }),
      ean3: new UntypedFormControl({ value: "" }),
      modified: new UntypedFormControl({ value: "", disabled: false }),
      productName: new UntypedFormControl("", Validators.required),
      presentation: new UntypedFormControl("", Validators.required),
      laboratory: new UntypedFormControl("", Validators.required),
      productType: new UntypedFormControl(null, Validators.required),
      productSubType: new UntypedFormControl(null, Validators.required),
      substance: new UntypedFormControl("", Validators.required),
      fiscalNumber: new UntypedFormControl(null, Validators.required),
      GGREMCode: new UntypedFormControl("", Validators.required),
      registry: new UntypedFormControl("", Validators.required),
      therapeuticClass: new UntypedFormControl(null, Validators.required),
      productUseValidity: new UntypedFormControl(null, Validators.required),
      priceRegime: new UntypedFormControl("", Validators.required),
      hospitalRestriction: new UntypedFormControl(false),
      cap: new UntypedFormControl(false),
      confaz: new UntypedFormControl(false),
      icms0Percent: new UntypedFormControl(false),
      appealAnalysis: new UntypedFormControl(""),
      tributaryCreditConcession: new UntypedFormControl("", Validators.required),
      commercialization: new UntypedFormControl(false),
      targe: new UntypedFormControl(""),
      useType: new UntypedFormControl("", Validators.required),
    });
  }

  public editProductFormGroup(): void {
    const formBuilder = new UntypedFormBuilder();
    this.formGroup = formBuilder.group({
      productKey: new UntypedFormControl(""),
      ean1: new UntypedFormControl({ value: "" }),
      ean2: new UntypedFormControl({ value: "" }),
      ean3: new UntypedFormControl({ value: "" }),
      modified: new UntypedFormControl({ value: "", disabled: true }),
      productName: new UntypedFormControl("", Validators.required),
      presentation: new UntypedFormControl("", Validators.required),
      laboratory: new UntypedFormControl("", Validators.required),
      productType: new UntypedFormControl(null),
      productSubType: new UntypedFormControl(null),
      substance: new UntypedFormControl("", Validators.required),
      fiscalNumber: new UntypedFormControl(null, Validators.required),
      GGREMCode: new UntypedFormControl("", Validators.required),
      registry: new UntypedFormControl("", Validators.required),
      therapeuticClass: new UntypedFormControl(null, Validators.required),
      productUseValidity: new UntypedFormControl(null),
      priceRegime: new UntypedFormControl("", Validators.required),
      hospitalRestriction: new UntypedFormControl(false),
      cap: new UntypedFormControl(false),
      confaz: new UntypedFormControl(false),
      icms0Percent: new UntypedFormControl(false),
      appealAnalysis: new UntypedFormControl(""),
      tributaryCreditConcession: new UntypedFormControl("", Validators.required),
      commercialization: new UntypedFormControl(false),
      targe: new UntypedFormControl(""),
      useType: new UntypedFormControl(""),
    });
  }

  public loadFormGroup(
    product: ProductLabelInfo,
    productTypes: ProductType[],
    productSubTypes: ProductSubtype[],
    productUseValidities: ProductUseValidity[],
    priceRegimes: PriceRegime[],
    useTypes: UseType[],
    tributaryCreditConcessions: TributaryCreditConcession[],
  ): void {
    const productType = productTypes.find((type) => type.getKey() === product.getProductType().getKey());
    const productSubType = productSubTypes.find((subType) => subType.getKey() === product.getProductSubType().getKey());
    const productUseValidity = productUseValidities.find((validity) => validity.getKey() === product.getProductUseValidity().getKey());
    const priceRegime = priceRegimes.find((regime) => regime.getKey() === product.getPriceRegime().getKey());
    const useType = useTypes.find((type) => type.getKey() === product.getUseType().getKey());
    const tributaryCreditConcession = tributaryCreditConcessions.find((concession) => concession.getKey() === product.getTributaryCreditConcession().getKey());
    this.formGroup.patchValue({
      productKey: product.getProductKey(),
      ean1: product.getEan1(),
      ean2: product.getEan2() ? product.getEan2() : "",
      ean3: product.getEan3() ? product.getEan3() : "",
      modified: product.getModified() ? product.getModified() : "",
      productName: product.getProductName(),
      presentation: product.getPresentation(),
      laboratory: product.getLaboratory(),
      productType: productType,
      productSubType: productSubType,
      substance: product.getSubstance(),
      fiscalNumber: product.getFiscalNumber().getMaskedValue(),
      therapeuticClass: product.getTherapeuticClass(),
      productUseValidity: productUseValidity,
      priceRegime: priceRegime,
      useType: useType,
      tributaryCreditConcession: tributaryCreditConcession,
      GGREMCode: product.getGGREMCode(),
      registry: product.getRegistry(),
      appealAnalysis: product.getAppealAnalysis(),
      targe: product.getTarge(),
      cap: product.getCap(),
      confaz: product.getConfaz(),
      icms0Percent: product.getIcms0Percent(),
      hospitalRestriction: product.getHospitalRestriction(),
      commercialization: product.getCommercialization(),
    });
  }

  public parseFormGroup(): ProductLabelInfo {
    this.formGroup.enable();
    const productLabelInfo = new ProductLabelInfo();
    productLabelInfo.setProductKey(this.formGroup.value.productKey);
    productLabelInfo.setEan1(this.formGroup.value.ean1);
    productLabelInfo.setEan2(this.formGroup.value.ean2);
    productLabelInfo.setEan3(this.formGroup.value.ean3);
    productLabelInfo.setModified(this.formGroup.value.modified);
    productLabelInfo.setProductName(this.formGroup.value.productName);
    productLabelInfo.setPresentation(this.formGroup.value.presentation);
    productLabelInfo.setLaboratory(this.formGroup.value.laboratory);
    productLabelInfo.setSubstance(this.formGroup.value.substance);
    productLabelInfo.setFiscalNumber(new FiscalNumber(this.formGroup.value.fiscalNumber));
    productLabelInfo.setGGREMCode(this.formGroup.value.GGREMCode);
    productLabelInfo.setRegistry(this.formGroup.value.registry);
    productLabelInfo.setAppealAnalysis(this.formGroup.value.appealAnalysis);
    productLabelInfo.setTarge(this.formGroup.value.targe);
    productLabelInfo.setCap(this.formGroup.value.cap);
    productLabelInfo.setConfaz(this.formGroup.value.confaz);
    productLabelInfo.setIcms0Percent(this.formGroup.value.icms0Percent);
    productLabelInfo.setHospitalRestriction(this.formGroup.value.hospitalRestriction);
    productLabelInfo.setCommercialization(this.formGroup.value.commercialization);
    productLabelInfo.setProductType(this.formGroup.value.productType);
    productLabelInfo.setProductSubType(this.formGroup.value.productSubType);
    productLabelInfo.setTherapeuticClass(this.formGroup.value.therapeuticClass);
    productLabelInfo.setProductUseValidity(this.formGroup.value.productUseValidity);
    productLabelInfo.setPriceRegime(this.formGroup.value.priceRegime);
    productLabelInfo.setUseType(this.formGroup.value.useType);
    productLabelInfo.setTributaryCreditConcession(this.formGroup.value.tributaryCreditConcession);
    return productLabelInfo;
  }

  public enableSubTypeFormControl(): void {
    this.formGroup.get("productSubType").enable();
  }

  public disableSubTypeFormControl(): void {
    this.formGroup.get("productSubType").disable();
  }

  public clearSubTypeFormControl(): void {
    this.formGroup.get("productSubType").reset();
  }
}
